import { AwaitQueue } from 'awaitqueue';
import type { types } from 'mediasoup-client';
import { serializeError } from 'serialize-error';
import type { ParsedInboundAudioStreamStats, ParsedInboundVideoStreamStats } from 'webrtc-issue-detector';
import { CONSUMER_CHECK_STATE_TIMEOUT } from '../../../constants/common';
import { PEER_EVENTS, TRACK_EVENTS } from '../../../constants/events';
import { SpatialLayer } from '../../../constants/videoConstrains';
import EnhancedEventEmitter from '../../../EnhancedEventEmitter';
import { isExpectedSignalingError } from '../../../errors/isExpectedSignalingError';
import OperationError from '../../../errors/OperationError';
import {
  type PeerTrackInfo, type PreferredLayersParams,
  type SpatialLayerParams, TrackLabel,
  type TrackOutboundStats,
} from '../../../types/common';
import type {
  PeerTrack as PublicPeerTrack,
  PeerTrackObserverEvents,
} from '../../../types/peerTrack';
import type { PeerConsumerFactory } from '../../../types/container';
import type { CallState } from '../../CallState';
import type Logger from '../../Logger';
import type Network from '../../network';
import type PeerConsumer from '../../PeerConsumer';
import InboundTrackStatsManager from '../../WebRTCStats/InboundTrackStatsManager';
import type StatsHandler from '../../WebRTCStats/StatsHandler';
import type Media from '../index';
import type { ExpectedVideoParams, MOSScores } from '../../../qoe/types';
import type { WIDStatsAdapter } from '../../../qoe/WIDStatsAdapter';
import type { ScoresCalculator } from '../../../qoe/ScoresCalculator';

export interface PeerTrackDependencies {
  callState: CallState;
  loggerFactory: (namespace: string) => Logger;
  media: Media;
  network: Network;
  peerConsumerFactory: PeerConsumerFactory;
  widStatsAdapter: WIDStatsAdapter;
  scoresCalculator: ScoresCalculator;
}

interface PeerTrackConstructor extends PeerTrackDependencies {
  mediaStreamTrack: MediaStreamTrack;
  label: TrackLabel;
  consumer?: PeerConsumer;
  muted: boolean;
  paused: boolean;
  onClose: () => void;
  peerEventEmitter: EnhancedEventEmitter;
  statsHandler: StatsHandler;
}

interface CloseTrackParams {
  closeRemote?: boolean;
}

class PeerTrack implements PublicPeerTrack {
  public readonly label: TrackLabel = TrackLabel.Unknown;

  readonly #network: Network;
  readonly #media: Media;

  readonly #onClose: () => void;

  readonly #peerConsumerFactory: PeerConsumerFactory;

  readonly #logger: Logger;

  readonly #peerEventEmitter: EnhancedEventEmitter;

  #mediaStreamTrack: MediaStreamTrack;

  #consumer?: PeerConsumer;

  #paused: boolean;

  #consumerRestarted = false;

  #checkStateTimeout?: ReturnType<typeof setTimeout>;

  #closed = false;

  readonly #queue = new AwaitQueue();

  #muted = false;

  #volumeLevel = 0;

  readonly #webrtcStatsManager: InboundTrackStatsManager;

  readonly observer = new EnhancedEventEmitter<PeerTrackObserverEvents>();

  constructor(payload: PeerTrackConstructor) {
    this.#mediaStreamTrack = payload.mediaStreamTrack;
    this.label = payload.label;
    this.#consumer = payload.consumer;
    this.#peerConsumerFactory = payload.peerConsumerFactory;
    this.#logger = payload.loggerFactory('PeerTrack');
    this.#peerEventEmitter = payload.peerEventEmitter;
    this.#muted = payload.muted;
    this.#paused = payload.paused;
    this.#network = payload.network;
    this.#media = payload.media;
    this.#onClose = payload.onClose;
    this.#webrtcStatsManager = new InboundTrackStatsManager({
      track: payload.mediaStreamTrack,
      statsHandler: payload.statsHandler,
      widStatsAdapter: payload.widStatsAdapter,
      scoresCalculator: payload.scoresCalculator,
      getExpectedVideoParams: (): ExpectedVideoParams | undefined => this.#consumer?.currentSpatialLayerParams,
    });
  }

  get consumer(): PeerConsumer | undefined {
    return this.#consumer;
  }

  get videoWebRTCStats(): ParsedInboundVideoStreamStats | undefined {
    return this.#webrtcStatsManager.videoWebRTCStats;
  }

  get audioWebRTCStats(): ParsedInboundAudioStreamStats | undefined {
    return this.#webrtcStatsManager.audioWebRTCStats;
  }

  get connectionScores(): MOSScores {
    return this.#webrtcStatsManager.scores;
  }

  get logCtx(): Record<string, unknown> {
    return {
      id: this.#mediaStreamTrack.id,
      label: this.label,
      consumerId: this.#consumer?.id,
      producerId: this.#consumer?.producerId,
      muted: this.#muted,
      paused: this.#paused,
      closed: this.#closed,
    };
  }

  get volume(): number {
    return this.#volumeLevel;
  }

  get isRemote(): boolean {
    return !!this.#consumer;
  }

  get mediaStreamTrack(): MediaStreamTrack {
    return this.#mediaStreamTrack;
  }

  get kind(): types.MediaKind {
    return this.mediaStreamTrack.kind as types.MediaKind;
  }

  get isPaused(): boolean {
    return this.#paused;
  }

  get isMuted(): boolean {
    return this.#muted;
  }

  get consumerId(): string | undefined {
    return this.#consumer?.id;
  }

  get currentSpatialLayer(): number {
    return this.#consumer?.currentSpatialLayer ?? SpatialLayer.LOW;
  }

  public async close({ closeRemote = true }: CloseTrackParams = {}): Promise<void> {
    if (this.#closed) {
      return;
    }

    if (this.#consumer) {
      await this.closeConsumer({ closeRemote });
      this.clearCheckStateTimeout();
    }

    this.#peerEventEmitter.safeEmit(PEER_EVENTS.trackEnd, this);
    this.#closed = true;
    this.#onClose();
  }

  public async mute(): Promise<void> {
    await this.#queue.push(async () => {
      this.#logger.debug('mute()', { track: this.logCtx });
      await this.pauseConsumer();
      this.#muted = true;
    });
  }

  public async unmute(): Promise<void> {
    await this.#queue.push(async () => {
      this.#logger.debug('unmute()', { track: this.logCtx });
      await this.resumeConsumer();
      this.#muted = false;
    });
  }

  public async pause(pauseRemote = true): Promise<void> {
    await this.#queue.push(async () => {
      if (!this.#consumer || this.isMuted) {
        this.#paused = true;
        this.#peerEventEmitter.safeEmit(PEER_EVENTS.trackPaused, this);
        this.#logger.debug('pause()', { track: this.logCtx });

        return;
      }

      await this.pauseConsumer(pauseRemote);
      this.#paused = true;
      this.#peerEventEmitter.safeEmit(PEER_EVENTS.trackPaused, this);
    });
  }

  public async resume(resumeRemote = true): Promise<void> {
    await this.#queue.push(async () => {
      if (!this.#consumer || this.isMuted) {
        this.#paused = false;
        this.#peerEventEmitter.safeEmit(PEER_EVENTS.trackResumed, this);
        this.#logger.debug('resume()', { track: this.logCtx });

        return;
      }

      await this.resumeConsumer(resumeRemote);
      this.#paused = false;
      this.#peerEventEmitter.safeEmit(PEER_EVENTS.trackResumed, this);
    });
  }

  private async resumeConsumer(resumeRemote = true): Promise<void> {
    if (!this.#consumer) {
      return;
    }

    try {
      this.#consumer.resume();

      if (resumeRemote) {
        await this.#network.resumeRemoteConsumer(this.#consumer.id);
      }

      this.checkConsumerState().catch((error: unknown) => {
        this.#logger.warn('Failed to checkConsumerState()', {
          track: this.logCtx,
          error: serializeError(error),
        });
      });
      this.#logger.debug('resumeConsumer()', { track: this.logCtx });
    } catch (error) {
      const logAction = isExpectedSignalingError(error)
        ? this.#logger.debug.bind(this.#logger)
        : this.#logger.warn.bind(this.#logger);
      logAction('resumeConsumer()', { error: serializeError(error), track: this.logCtx });
    }
  }

  private async pauseConsumer(pauseRemote = true): Promise<void> {
    if (!this.#consumer) {
      return;
    }

    try {
      this.clearCheckStateTimeout();

      if (pauseRemote) {
        await this.#network.pauseRemoteConsumer(this.#consumer.id);
      }

      if (this.kind === 'audio') {
        this.#logger.debug('pauseConsumer() - skipping for audio', { track: this.logCtx });

        return;
      }

      this.#consumer.pause();
      this.#consumerRestarted = false;
      this.#logger.debug('pauseConsumer()', { track: this.logCtx });
    } catch (error) {
      const logAction = isExpectedSignalingError(error)
        ? this.#logger.debug.bind(this.#logger)
        : this.#logger.warn.bind(this.#logger);
      logAction('pauseConsumer()', { error: serializeError(error), track: this.logCtx });
    }
  }

  public async requestVideoPreferredLayers({ spatialLayer, temporalLayer }: PreferredLayersParams): Promise<void> {
    if (!this.#consumer) {
      return;
    }

    try {
      if (this.#consumer.isAudio) {
        this.#logger.debug('Only video consumers support layers', { case: 'requestVideoPreferredLayers' });

        return;
      }

      if (spatialLayer === this.#consumer.currentSpatialLayer) {
        return;
      }

      if (spatialLayer > this.#consumer.spatialLayers) {
        this.#logger.debug('Invalid spatial layer', {
          spatialLayer,
          consumerSpatialLayers: this.#consumer.spatialLayers,
          case: 'requestVideoPreferredLayers',
        });

        return;
      }

      if (spatialLayer > this.#consumer.currentMaxSpatialLayer) {
        await this.requestMaxSpatialLayer(this.#consumer.id, spatialLayer);

        return;
      }

      await this.#network.updateConsumerPreferredLayers(this.#consumer.id, spatialLayer, temporalLayer);

      this.#logger.debug('requestVideoPreferredLayers()', {
        track: this.logCtx,
        consumer: this.#consumer,
        spatialLayer,
        temporalLayer,
      });
    } catch (error) {
      const logAction = isExpectedSignalingError(error)
        ? this.#logger.debug.bind(this.#logger)
        : this.#logger.error.bind(this.#logger);
      logAction('Failed to request preferred layers', {
        case: 'requestVideoPreferredLayers',
        track: {
          id: this.mediaStreamTrack.id,
          kind: this.mediaStreamTrack.kind,
          label: this.mediaStreamTrack.label,
        },
        consumerId: this.#consumer.id,
        spatialLayers: this.#consumer.spatialLayers,
        temporalLayers: this.#consumer.temporalLayers,
        error: serializeError(error),
      });
      throw new OperationError('peer_track_request_preferred_layers_failed', error);
    }
  }

  public async requestKeyFrame(): Promise<void> {
    if (!this.consumerId) {
      return;
    }

    this.#logger.debug('requestKeyFrame()', { track: this.logCtx });

    try {
      await this.#network.requestConsumerKeyFrame(this.consumerId);
    } catch (error: unknown) {
      const logAction = isExpectedSignalingError(error)
        ? this.#logger.debug.bind(this.#logger)
        : this.#logger.error.bind(this.#logger);
      logAction('requestKeyFrame()', {
        error: serializeError(error),
        track: this.logCtx,
        consumerId: this.consumerId,
      });
      throw new OperationError('peer_track_request_key_frame_failed', error);
    }
  }

  public getVideoAvailableLayers(): SpatialLayerParams[] | undefined {
    if (!this.#consumer) {
      return undefined;
    }

    if (this.#consumer.isAudio) {
      this.#logger.debug('Only video consumers support layers', { case: 'getVideoAvailableLayers' });

      return undefined;
    }

    return this.#consumer.availableSpatialLayers;
  }

  async getInfo(): Promise<PeerTrackInfo> {
    const {
      width, height, frameRate, aspectRatio,
    } = this.#mediaStreamTrack.getSettings();

    return {
      trackId: this.#mediaStreamTrack.id,
      readyState: this.#mediaStreamTrack.readyState,
      isRemote: this.isRemote,
      kind: this.kind,
      label: this.label,
      width,
      height,
      frameRate,
      aspectRatio,
      paused: this.#paused,
      trackInboundStats: await this.#consumer?.getStats(),
      trackOutboundStats: await this.getOutboundStats(),
    };
  }

  private async getOutboundStats(): Promise<TrackOutboundStats | undefined> {
    if (this.isRemote) {
      return undefined;
    }

    const outboundTrack = this.#media.getAllTracks().find((track) => track.getLabel() === this.label);

    if (!outboundTrack) {
      return undefined;
    }

    return await outboundTrack.getStats();
  }

  private async requestMaxSpatialLayer(consumerId: string, spatialLayer: number): Promise<void> {
    try {
      if (!this.#consumer) {
        return;
      }

      const cantRequest = this.#consumer.isAudio
        || this.#consumer.spatialLayers === 0
        || this.#consumer.currentMaxSpatialLayer === spatialLayer
        || spatialLayer < this.#consumer.currentMaxSpatialLayer;

      if (cantRequest) {
        this.#logger.warn('Can`t request this spatial layer', {
          case: 'requestMaxSpatialLayer',
          consumerId,
          spatialLayer,
        });

        return;
      }

      this.#consumer.setRequestedSpatialLayer(spatialLayer);
      await this.#network.requestProducerMaxSpatialLayer(this.#consumer.producerId, spatialLayer);

      this.#logger.debug('requestMaxSpatialLayer()', {
        consumerId,
        spatialLayer,
      });
    } catch (error) {
      const logAction = isExpectedSignalingError(error)
        ? this.#logger.debug.bind(this.#logger)
        : this.#logger.warn.bind(this.#logger);
      logAction('Failed to request max spatial layer', {
        case: 'requestMaxSpatialLayer',
        consumerId,
        spatialLayer,
        error: serializeError(error),
      });
    }
  }

  private async checkConsumerState(): Promise<void> {
    try {
      if (this.#closed || this.kind === 'audio') {
        return;
      }

      this.#logger.debug('checkConsumerState()', { track: this.logCtx });
      const isInboundRTPStreamActive = await this.isInboundRTPStreamActive();

      if (isInboundRTPStreamActive || this.#closed) {
        return;
      }

      await this.requestKeyFrame();
      const isActiveAfterKeyFrameRequest = await this.isInboundRTPStreamActive();

      if (isActiveAfterKeyFrameRequest || this.#closed) {
        return;
      }

      if (this.#consumerRestarted) {
        this.#peerEventEmitter.emit(PEER_EVENTS.trackFailed, this);
        this.#logger.debug('trackFail()', { track: this.logCtx });

        return;
      }

      await this.restartConsumer();
    } catch (error) {
      this.#logger.warn('checkConsumerState()', { track: this, error });
    }
  }

  setVolume(value: number): void {
    if (this.#volumeLevel === value) {
      return;
    }

    this.#volumeLevel = value;
    this.observer.safeEmit(TRACK_EVENTS.volumeChanged, { value });
  }

  private async closeConsumer({ closeRemote = true }: CloseTrackParams = {}): Promise<void> {
    if (!this.#consumer) {
      return;
    }

    if (closeRemote) {
      await this.#network.closeRemoteConsumer(this.#consumer.id);
    }

    this.#consumer.close();
  }

  private async restartConsumer(): Promise<void> {
    try {
      if (!this.#consumer || this.#closed) {
        return;
      }

      this.#logger.debug('restartConsumer()', { track: this.logCtx });
      this.#peerEventEmitter.emit(PEER_EVENTS.trackEnd, this);
      this.#paused = true;
      await this.closeConsumer();
      const { consumer } = await this.#network.createConsumer({
        producerId: this.#consumer.producerId,
        rtpCapabilities: this.#media.mediasoupDevice.rtpCapabilities,
        producerPeerId: this.#consumer.appData.producerData.peerId,
      });

      this.#consumerRestarted = true;
      this.#consumer = this.#peerConsumerFactory({
        consumer,
      });
      this.#mediaStreamTrack = consumer.track;

      await this.resume();
      this.#peerEventEmitter.emit(PEER_EVENTS.trackStart, this);
    } catch (error) {
      this.#consumerRestarted = true;
      this.#logger.warn('restartConsumer()', { track: this, error });
    }
  }

  private async isInboundRTPStreamActive(): Promise<boolean> {
    if (!this.#consumer || this.#closed) {
      return false;
    }

    const { framesDecoded } = await this.#consumer.getInboundRTPStreamStats();

    return await new Promise((resolve) => {
      this.#checkStateTimeout = setTimeout(async () => {
        try {
          if (!this.#consumer) {
            resolve(false);

            return;
          }

          const { framesDecoded: nowFramesDecoded } = await this.#consumer.getInboundRTPStreamStats();

          if (framesDecoded === nowFramesDecoded && !this.isPaused) {
            resolve(false);

            return;
          }

          resolve(true);
        } catch (error) {
          this.#logger.debug('isInboundRTPStreamActive()', { error, track: this.logCtx });
          resolve(false);
        }
      }, CONSUMER_CHECK_STATE_TIMEOUT);
    });
  }

  private clearCheckStateTimeout(): void {
    if (this.#checkStateTimeout) {
      return;
    }

    clearTimeout(this.#checkStateTimeout);
  }
}

export default PeerTrack;
